import { Fragment } from "react";
import classNames from "classnames";
import { Avatar } from "@design-system/Avatar/Avatar";
import {
    AvatarGroup,
    type AvatarGroupSize,
} from "@design-system/Avatar/AvatarGroup";
import { VStack } from "@design-system/VStack/VStack";
import {
    avatarGroupSizes,
    avatarSizePixels,
    groupCounts,
    mixedStackPeople,
    playgroundClasses,
    playgroundEntities,
    playgroundIntegrations,
    playgroundPeople,
    type PlaygroundPerson,
} from "./AvatarPlayground.lib";
import styles from "./AvatarPlayground.module.scss";

const surfaces = [
    { id: "white", label: "White", className: styles.surfaceWhite },
    {
        id: "neutral",
        label: "var(--bg-neutral)",
        className: styles.surfaceNeutral,
    },
    {
        id: "hoveredRow",
        label: "Hovered table row",
        className: styles.surfaceHoveredRow,
    },
] as const;

/** Each row kind of the group matrix; integrations carry the plan card's "+50". */
const stackKinds = [
    { id: "people", people: playgroundPeople, extraCount: 0 },
    { id: "entities", people: playgroundEntities, extraCount: 0 },
    { id: "classes", people: playgroundClasses, extraCount: 0 },
    { id: "mixed", people: mixedStackPeople, extraCount: 0 },
    { id: "logos +50", people: playgroundIntegrations, extraCount: 50 },
] as const;

const renderPerson = (person: PlaygroundPerson) => <Avatar {...person} />;

/** A playground stack over the shared samples, with the same key and label getters everywhere. */
export const PlaygroundAvatarGroup = ({
    people,
    size,
    extraCount,
}: {
    people: readonly PlaygroundPerson[];
    size: AvatarGroupSize;
    extraCount?: number;
}) => (
    <AvatarGroup
        items={people}
        getKey={(person) => person.id}
        getLabel={(person) => person.name}
        renderAvatar={renderPerson}
        size={size}
        extraCount={extraCount}
    />
);

const SurfaceGrid = ({
    label,
    className,
}: {
    label: string;
    className: string;
}) => (
    <VStack gap={8}>
        <h3 className={styles.stateTitle}>{label}</h3>
        <div className={classNames(styles.grid, styles.groupGrid, className)}>
            <span className={styles.columnHeader}>stack · size</span>
            {groupCounts.map(({ label: countLabel }) => (
                <span key={countLabel} className={styles.columnHeader}>
                    {countLabel}
                </span>
            ))}
            {stackKinds.map((kind) =>
                avatarGroupSizes.map((size) => (
                    <Fragment key={`${kind.id}-${size}`}>
                        <span className={styles.rowLabel}>
                            {`${kind.id} · ${size} · ${avatarSizePixels[size]}px`}
                        </span>
                        {groupCounts.map(({ label: countLabel, count }) => (
                            <span key={countLabel} className={styles.cell}>
                                <PlaygroundAvatarGroup
                                    people={kind.people.slice(0, count)}
                                    size={size}
                                    extraCount={kind.extraCount}
                                />
                            </span>
                        ))}
                    </Fragment>
                )),
            )}
        </div>
    </VStack>
);

/**
 * Figma UserGroup and EntityGroup 1 / 2 / 3 / 4 / 4+ at every group size, plus
 * class, mixed and plan-card stacks, on the three surfaces stacks sit on.
 */
export const AvatarGroupMatrix = () => (
    <section className={styles.matrix}>
        <VStack gap={4}>
            <h2 className={styles.sectionTitle}>Group matrix</h2>
            <span className={styles.sectionDescription}>
                Figma draws xs only. Avatars keep their full size and the seam
                is transparent, so each surface shows through it; each avatar is
                cut along the next avatar&apos;s outer shape grown by the seam:
                a circle, a square&apos;s edge and rounded corners, or a
                diamond&apos;s rounded point. xs class stacks don&apos;t
                overlap. The logos row adds extraCount=50 to the chip.
            </span>
        </VStack>
        {surfaces.map((surface) => (
            <SurfaceGrid
                key={surface.id}
                label={surface.label}
                className={surface.className}
            />
        ))}
    </section>
);
